import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sparkles, Brain, Target, Award, Layers, BarChart4, CircleUserRound, BarChart } from 'lucide-react';
import {
  RateReview,
  Timeline,
  MenuBook,
  Group,
  Insights,
  Visibility,
  School
} from "@mui/icons-material";


const FeaturesCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const features = [
    {
      title: "Generate Feedback Comments",
      description: "Generate detailed, personalized feedback comments that reflect student understanding and growth.",
      icon: <RateReview className="w-12 h-12 text-primary" />
    },
    {
      title: "Assessment based on DOK & Proficiency Scale",
      description: "Incorporate depth of knowledge levels & proficiency scale grading into your assessment.",
      icon: <Timeline className="w-12 h-12 text-primary" />
    },
    {
      title: "Curriculum Builder",
      description: "Design and manage subjects, units, and DOK-level skills in a centralized flow.",
      icon: <MenuBook className="w-12 h-12 text-primary" />
    },
    {
      title: "Assess Multiple Students",
      description: "Record your assessment for all students efficiently in a few clicks.",
      icon: <Group className="w-12 h-12 text-primary" />
    },
    {
      title: "Smart Student Analytics",
      description: "Visualize learning gaps and strengths across DOK levels and subjects using dynamic charts.",
      icon: <Insights className="w-12 h-12 text-primary" />
    },
    {
      title: "Student Self-View",
      description: "Allow students to log in securely and view only their personalized report cards.",
      icon: <Visibility className="w-12 h-12 text-primary" />
    },
    {
      title: "Teacher Resources",
      description: "Generate unit plans, lesson plans, class activities, rubrics, and more, centered around BC curriculum.",
      icon: <School className="w-12 h-12 text-primary" />
    }
  ];

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % features.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + features.length) % features.length);
  };

  return (
    <section id="features" className="py-20 bg-secondary/10">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-primary mb-4">
            Powerful Features
          </h2>
          <p className="text-xl text-primary/80 max-w-3xl mx-auto">
            Everything you need to transform your assessment process
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          <div className="overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="bg-cream rounded-3xl p-12 border border-secondary/30 shadow-lg hover:shadow-xl transition-shadow duration-200"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="bg-primary/10 rounded-full p-6 mb-8">
                    {features[currentIndex].icon}
                  </div>
                  <h3 className="text-3xl font-bold text-primary mb-6">
                    {features[currentIndex].title}
                  </h3>
                  <p className="text-xl text-primary/80 max-w-2xl">
                    {features[currentIndex].description}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 bg-primary text-secondary p-3 rounded-full hover:bg-primary/90 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 bg-primary text-secondary p-3 rounded-full hover:bg-primary/90 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="flex justify-center gap-2 mt-8">
            {features.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-colors ${index === currentIndex ? 'bg-primary' : 'bg-primary/30'
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesCarousel;
